import { useState } from "react";
import type { FC } from "react";
import { useQuery, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import Stack from "@mui/material/Stack";
import {
  responsibleAccessErrorWithCode,
  ResponsibleAccessErrorCodes,
  type ConnectedTenant,
  type RegistrationById,
} from "../../../api/registrations.types";
import { ResponsibleLosesAccessDialog } from "./ResponsibleLosesAccessDialog";
import { DynamicForm } from "../../../dynamicForm";
import { OwnControllerLabel, TenantsSelect } from "../../../shared/TenantsSelect";
import { useCurrentCustomer } from "../../../root/CurrentOrganisationProvider";
import { usePermission } from "../../../shared/permissions";
import {
  myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions,
  type SetConnectedTenantsPayload,
} from "../../../api/registrations";
import { useRegistrationVariant } from "../RegistrationVariantProvider";

type ConnectedTenantsProps = {
  registration: RegistrationById;
  setConnectedTenantsMutation: UseMutationResult<unknown, Error, SetConnectedTenantsPayload>;
  isReadOnly: boolean;
};

type PendingConfirmation = { tenants: Array<ConnectedTenant>; unitsResponsibleCannotAccess: Array<string> };

const idsOf = (tenants: ReadonlyArray<ConnectedTenant>) => tenants.map(tenant => tenant.id);

const sameTenants = (a: ReadonlyArray<ConnectedTenant>, b: ReadonlyArray<ConnectedTenant>) =>
  a.length === b.length && idsOf(a).sort().join() === idsOf(b).sort().join();

export const ConnectedTenants: FC<ConnectedTenantsProps> = ({
  registration,
  setConnectedTenantsMutation,
  isReadOnly,
}) => {
  const { t, formatList } = useTranslation();
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const takesOneUnit = variant === "Processor";
  const { hasPermission } = usePermission();
  const { inputState, setIsEditing } = DynamicForm.useInlineInputState(isReadOnly);

  const [draft, setDraft] = useState<Array<ConnectedTenant> | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);

  const [wasHovered, setWasHovered] = useState(false);
  const shouldQuery = inputState !== "readonly" && (inputState === "editing" || wasHovered);
  const tenantHierarchyQuery = useQuery({
    ...myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions(),
    enabled: shouldQuery,
  });

  const cannotBeResponsibleError = responsibleAccessErrorWithCode(
    setConnectedTenantsMutation.error,
    ResponsibleAccessErrorCodes.youCannotBeResponsibleInEveryTenant
  );

  const commit = (tenants: Array<ConnectedTenant>) => {
    if (setConnectedTenantsMutation.isPending || pendingConfirmation) return;

    if (sameTenants(tenants, registration.connectedTenants)) {
      setDraft(null);
      return;
    }

    setConnectedTenantsMutation.mutate(
      { registrationId: registration.id, tenantIds: idsOf(tenants), isConfirm: !registration.responsible },
      {
        onSuccess: () => setDraft(null),
        onError: error => {
          const responsibleHasNoAccessError = responsibleAccessErrorWithCode(
            error,
            ResponsibleAccessErrorCodes.responsibleHasNoAccessToNewTenant
          );
          if (responsibleHasNoAccessError) {
            setPendingConfirmation({
              tenants,
              unitsResponsibleCannotAccess: responsibleHasNoAccessError.tenantsResponsibleCannotAccess,
            });
            setConnectedTenantsMutation.reset();
            return;
          }

          setDraft(null);
        },
      }
    );
  };

  const draftIsEmpty = draft !== null && draft.length === 0;

  const save = () => {
    setIsEditing(false);

    if (draft === null) return;

    if (draft.length === 0) {
      setDraft(null);
      return;
    }

    commit(draft);
  };

  const changeDraft = (tenants: Array<ConnectedTenant>) => {
    setDraft(tenants);

    if (setConnectedTenantsMutation.isError) setConnectedTenantsMutation.reset();

    if (takesOneUnit) commit(tenants);
  };

  const confirmDespiteResponsible = () => {
    if (!pendingConfirmation) return;

    setConnectedTenantsMutation.mutate(
      { registrationId: registration.id, tenantIds: idsOf(pendingConfirmation.tenants), isConfirm: true },
      {
        onSuccess: () => {
          setPendingConfirmation(null);
          setDraft(null);
        },
        onError: () => {
          setPendingConfirmation(null);
          setDraft(null);
        },
      }
    );
  };

  const cancelConfirm = () => {
    setPendingConfirmation(null);
    setDraft(null);
    setConnectedTenantsMutation.reset();
  };

  return (
    <DynamicForm.InlineFieldPrimary
      htmlFor="connectedTenants"
      label={t(`RegistrationDrawer.ConnectedTenant.${variant}.Label`)}
      infoPopoverContent={{
        title: t("RegistrationDrawer.ConnectedTenant.InfoPopoverHeader"),
        body: t("RegistrationDrawer.ConnectedTenant.InfoPopoverContent"),
      }}
      input={
        <Stack sx={{ width: "100%" }}>
          <TenantsSelect
            id="connectedTenants"
            tenantHierarchy={tenantHierarchyQuery.data}
            selectedTenants={draft ?? registration.connectedTenants}
            onChange={changeDraft}
            renderOptionEndAdornment={option =>
              currentCustomer.customerKind !== "enterprise" &&
              variant === "Controller" &&
              option.isPersonalDataController && (
                <OwnControllerLabel>{t("RegistrationDrawer.Connected.Controller.Label")}</OwnControllerLabel>
              )
            }
            getChipSecondaryLabel={tenant =>
              currentCustomer.customerKind !== "enterprise" && variant === "Controller"
                ? tenant.personalDataController?.name
                : undefined
            }
            disabledTenant={tenant => !hasPermission("nova-forms-api:registration:create", tenant.permissions)}
            disabledTooltip={t("CreateRegistration.Dialog.MissingPermissionsOnTenantTooltip")}
            singleSelect={takesOneUnit}
            keepListOpenOnSelect
            stackValues
            disabled={isReadOnly || setConnectedTenantsMutation.isPending}
            error={setConnectedTenantsMutation.isError || draftIsEmpty}
            inputState={inputState}
            inputRootSx={DynamicForm.inlineInputStateStyles[inputState]}
            onBlur={save}
            onFocus={() => setIsEditing(true)}
            onMouseOver={() => setWasHovered(true)}
          />
          {draftIsEmpty ? (
            <DynamicForm.ErrorMessage errorMessage={t("CreateRegistration.Dialog.TenantError.Several")} />
          ) : cannotBeResponsibleError ? (
            <DynamicForm.ErrorMessage
              errorMessage={t("RegistrationDrawer.ConnectedTenant.YouCannotBeResponsibleError", {
                units: formatList(cannotBeResponsibleError.tenantsResponsibleCannotAccess),
              })}
            />
          ) : (
            setConnectedTenantsMutation.isError && <DynamicForm.ErrorMessage />
          )}
          {pendingConfirmation && registration.responsible && (
            <ResponsibleLosesAccessDialog
              context="changeConnectedTenants"
              registrationName={registration.name}
              responsibleName={`${registration.responsible.firstName} ${registration.responsible.lastName}`}
              unitsResponsibleCannotAccess={pendingConfirmation.unitsResponsibleCannotAccess}
              isSubmitting={setConnectedTenantsMutation.isPending}
              onConfirm={confirmDespiteResponsible}
              onCancel={cancelConfirm}
            />
          )}
        </Stack>
      }
    />
  );
};
